import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, apiRequest } from '../api/client.js';
import type { ModerationPlatformCapabilities } from '../api/types.js';
import { EmptyState } from './EmptyState.js';
import { PlatformIcon } from './PlatformIcon.js';
import { ACTION_LABEL, CAPABILITY_LABEL, isCustomerFixable } from './moderation-labels.js';
import { platformLabel } from '../platforms/labels.js';

/**
 * What Connaro can actually do, per connected platform (ARCHITECTURE.md §98.6).
 *
 * ============================ WHY THIS EXISTS ============================
 * Moderation is NOT uniform across platforms and never will be — providers
 * offer different APIs, grant different scopes, and gate some behind review.
 * §18 forbids implying otherwise, and the capability model exists so Connaro
 * can be specific about it.
 *
 * None of that reached the customer. The endpoint was built and wired to
 * nothing, so a workspace could learn what a platform supports only by opening
 * an individual finding — which needs a rule, and a message matching it. On an
 * empty queue, where every workspace starts, there was no way to find out.
 *
 * This panel is the answer to "which platforms support moderation?", asked
 * before anything has gone wrong rather than after.
 * =======================================================================
 */


export function ModerationCapabilities(): React.JSX.Element {
  const [platforms, setPlatforms] = useState<ModerationPlatformCapabilities[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiRequest<{ platforms: ModerationPlatformCapabilities[] }>(
        '/moderation/capabilities',
      );
      setPlatforms(data.platforms);
    } catch (cause) {
      setPlatforms([]);
      setError(
        cause instanceof ApiError ? cause.message : 'Connaro could not read platform capabilities.',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="panel capabilities" aria-labelledby="capabilities-heading">
      <div className="panel__header">
        <h2 className="panel__title" id="capabilities-heading">
          What Connaro can do on each platform
        </h2>
      </div>

      <div className="panel__body">
        <p className="field__hint capabilities__lede">
          {/*
           * Said once, at the top, rather than implied by a wall of states.
           * A customer reading four "not built yet" rows deserves to know that
           * is a real property of the product and not a fault in their setup.
           */}
          Moderation is not the same everywhere. Each platform offers different controls, and some
          need permissions you grant when you connect.
        </p>

        {error === null ? null : <p className="detail__unavailable">{error}</p>}

        {platforms === null ? (
          <p>Loading…</p>
        ) : platforms.length === 0 ? (
          <EmptyState title="Nothing connected yet">
            <p>
              No platforms are connected to this workspace, so there is nothing to moderate. Connect
              one and its capabilities appear here.
            </p>
            <Link className="btn btn--sm" to="/connections">
              Go to Connections
            </Link>
          </EmptyState>
        ) : (
          <ul className="capabilities__list">
            {platforms.map((entry) => {
              const name = platformLabel(entry.platform);
              /*
               * One remedy per platform, not one per action. YouTube needs a
               * single reconnect to fix three actions at once, and repeating
               * the link three times would read as three separate chores.
               */
              const reconnectable = entry.actions.some((action) => isCustomerFixable(action.state));

              /*
               * Where every action gives the SAME state AND the same note,
               * say it once.
               *
               * A platform Connaro has not built repeats one identical
               * sentence four times, which buries the platforms whose four
               * answers genuinely differ. Both halves of the check matter:
               * Discord's actions share a state but not a remedy — "delete it
               * in Discord" and "ban them in Discord" are different
               * instructions, and collapsing on state alone would discard
               * them.
               */
              const first = entry.actions[0];
              const uniform =
                first !== undefined &&
                entry.actions.length > 1 &&
                entry.actions.every(
                  (action) => action.state === first.state && action.note === first.note,
                );

              return (
                <li key={entry.platformConnectionId} className="capabilities__platform">
                  <div role="group" aria-labelledby={`cap-${entry.platformConnectionId}`}>
                    <h3 className="capabilities__name" id={`cap-${entry.platformConnectionId}`}>
                      <PlatformIcon platform={entry.platform} displayName={name} size={18} />
                      {name}
                    </h3>

                    {/*
                     * `uniform` already proves `first` is defined — it is one
                     * of its conjuncts — and TypeScript narrows through the
                     * alias, so re-checking here is dead code.
                     */}
                    {uniform ? (
                      /*
                       * `--single` opts this list out of stretching its rows.
                       * A collapsed platform has ONE row, and a row told to
                       * fill an equalised card becomes a single band the
                       * height of four — symmetric with nothing, and unlike
                       * every other card in the view.
                       */
                      <ul className="capabilities__actions capabilities__actions--single">
                        <li className="capabilities__action">
                          <span className="capabilities__action-name">Moderation</span>
                          <span className="capabilities__state">
                            {CAPABILITY_LABEL[first.state]}
                          </span>
                          <span className="capabilities__note">{first.note}</span>
                        </li>
                      </ul>
                    ) : (
                      <ul className="capabilities__actions">
                        {entry.actions.map((action) => (
                          <li
                            key={action.action}
                            className={`capabilities__action${
                              action.state === 'SUPPORTED' ? ' capabilities__action--ok' : ''
                            }`}
                          >
                            <span className="capabilities__action-name">
                              {ACTION_LABEL[action.action]}
                            </span>
                            {/*
                             * The state in words, always. §65 forbids meaning
                             * carried by appearance, and "greyed out" is exactly
                             * that — the reason has to be readable.
                             */}
                            <span className="capabilities__state">
                              {CAPABILITY_LABEL[action.state]}
                            </span>
                            {action.state === 'SUPPORTED' ? null : (
                              <span className="capabilities__note">{action.note}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/*
                     * The remedy slot is ALWAYS present, occupied or not.
                     *
                     * Cards are equal height and the action list absorbs
                     * whatever the footer leaves, so a card with a Reconnect
                     * button had 36px less to share out — its four rows came
                     * to 57.5px against 66.5px on a card without one. Reserving
                     * the slot is what makes rows match across the whole view
                     * rather than only within a column.
                     *
                     * Reserved by a copy of the BUTTON, hidden, rather than by
                     * a pixel height: it is the same element with the same
                     * padding, border and font, so restyling the button moves
                     * the reservation with it. `aria-hidden` and
                     * `visibility: hidden` keep it out of the accessibility
                     * tree and the tab order — it is spacing, and it must not
                     * announce a remedy that does not apply.
                     */}
                    <p className="capabilities__remedy">
                      {reconnectable ? (
                        <Link className="btn btn--sm" to="/connections">
                          Reconnect {name} on Connections
                        </Link>
                      ) : (
                        <span
                          className="btn btn--sm capabilities__remedy--ghost"
                          aria-hidden="true"
                        >
                          &nbsp;
                        </span>
                      )}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
